import FileSystemError from 'devextreme/file_management/error';
import type FileSystemItem from 'devextreme/file_management/file_system_item';
import { apiFetch, readErrorMessage } from '../api/client';
import { transferJobUrl, transferUrl } from './fileBrowserApi';
import {
  buildTransferRequest,
  type MountRoute,
  type TransferRequest
} from './createFileBrowserProvider';
import {
  conflictChoiceToPolicy,
  isTransferBatchCancelled,
  resetTransferBatchState,
  resolveFileConflict,
  type OnConflictPolicy
} from './fileConflictDialog';

const FILE_MANAGER_ERROR = {
  NoAccess: 0,
  FileExists: 1,
  FileNotFound: 2,
  DirectoryExists: 3,
  DirectoryNotFound: 4,
  Other: 32767
} as const;

function fileManagerError(message: string, item?: FileSystemItem, code: number = FILE_MANAGER_ERROR.Other): FileSystemError {
  return new FileSystemError(code, item, message);
}

function errorCodeFromStatus(status: number, conflictCode: number): number {
  if (status === 401 || status === 403) return FILE_MANAGER_ERROR.NoAccess;
  if (status === 404) return FILE_MANAGER_ERROR.FileNotFound;
  if (status === 409) return conflictCode;
  return FILE_MANAGER_ERROR.Other;
}

async function rejectFromResponse(
  res: Response,
  item: FileSystemItem | undefined,
  conflictCode: number
): Promise<never> {
  const message = await readErrorMessage(res);
  throw fileManagerError(message, item, errorCodeFromStatus(res.status, conflictCode));
}

async function pollTransferJob(jobId: string, item: FileSystemItem): Promise<void> {
  const deadline = Date.now() + 30 * 60 * 1000;
  while (Date.now() < deadline) {
    const res = await apiFetch(transferJobUrl(jobId));
    if (!res.ok) {
      throw fileManagerError(await readErrorMessage(res), item, FILE_MANAGER_ERROR.Other);
    }

    const job = (await res.json()) as { state?: string; errorText?: string };
    const state = job.state?.toLowerCase();
    if (state === 'succeeded') return;
    if (state === 'failed') {
      throw fileManagerError(job.errorText ?? 'Transfer failed.', item, FILE_MANAGER_ERROR.Other);
    }
    if (state === 'cancelled') {
      throw fileManagerError('Transfer cancelled.', item, FILE_MANAGER_ERROR.NoAccess);
    }

    await sleep(1000);
  }

  throw fileManagerError('Transfer timed out.', item, FILE_MANAGER_ERROR.Other);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function executeTransfer(
  request: TransferRequest,
  item: FileSystemItem,
  onConflict?: OnConflictPolicy
): Promise<void> {
  const conflictCode = item.isDirectory ? FILE_MANAGER_ERROR.DirectoryExists : FILE_MANAGER_ERROR.FileExists;
  const body = onConflict ? { ...request, onConflict } : request;

  const res = await apiFetch(transferUrl(), {
    method: 'POST',
    body: JSON.stringify(body)
  });

  if (res.status === 202) {
    const data = (await res.json()) as { jobId?: string };
    if (data.jobId) {
      await pollTransferJob(data.jobId, item);
      return;
    }
  }

  if (res.status === 409) {
    const payload = (await res.json().catch(() => ({}))) as { conflict?: boolean; errorText?: string };
    if (payload.conflict) {
      const choice = await resolveFileConflict(item.name);
      if (choice === 'skip') return;
      if (choice === 'cancel') {
        throw fileManagerError('Transfer cancelled.', item, FILE_MANAGER_ERROR.NoAccess);
      }
      const policy = conflictChoiceToPolicy(choice);
      if (!policy) {
        throw fileManagerError('Transfer cancelled.', item, FILE_MANAGER_ERROR.NoAccess);
      }
      return executeTransfer(request, item, policy);
    }
  }

  if (res.ok || res.status === 204) return;
  await rejectFromResponse(res, item, conflictCode);
}

export async function performFileTransfer(
  item: FileSystemItem,
  destinationDirectory: FileSystemItem,
  routes: readonly MountRoute[],
  operation: 'Copy' | 'Move'
): Promise<void> {
  if (isTransferBatchCancelled()) {
    throw fileManagerError('Transfer cancelled.', item, FILE_MANAGER_ERROR.NoAccess);
  }

  let request: TransferRequest;
  try {
    request = buildTransferRequest(item, destinationDirectory, routes, operation);
  } catch (error) {
    throw fileManagerError(error instanceof Error ? error.message : String(error), item, FILE_MANAGER_ERROR.NoAccess);
  }

  await executeTransfer(request, item);
}

export async function performFileTransfers(
  items: readonly FileSystemItem[],
  destinationDirectory: FileSystemItem,
  routes: readonly MountRoute[],
  operation: 'Copy' | 'Move'
): Promise<void> {
  resetTransferBatchState();
  for (const item of items) {
    await performFileTransfer(item, destinationDirectory, routes, operation);
  }
}
